import { useState, useEffect, useCallback, FC } from "react";
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    LinearProgress,
    Chip,
    IconButton,
    useTheme,
    alpha,
    styled,
    CircularProgress,
} from "@mui/material";
import {
    People as PeopleIcon,
    AccessTime as AccessTimeIcon,
    TrendingUp as TrendingUpIcon,
    BarChart as BarChartIcon,
    Schedule as ScheduleIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Refresh as RefreshIcon,
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon,
    Business as BusinessIcon,
    ShowChart as ShowChartIcon,
    CompareArrows as CompareArrowsIcon,
    Warning as WarningIcon,
} from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { format, addDays, subDays, isSameDay, differenceInDays, startOfDay, endOfDay } from "date-fns";
import { it, de, fr, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { shiftApi } from "api";
import { ShiftStatisticsEntity } from "api/generated";

const StyledCard = styled(Card)(({ theme }) => ({
    height: "100%",
    borderRadius: 12,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    transition: "all 0.3s ease",
    "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    },
}));

const BetaChip = styled(Chip)(({ theme }) => ({
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    fontWeight: "bold",
    fontSize: "0.6rem",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    boxShadow: "0 2px 8px rgba(102, 126, 234, 0.4)",
    height: "20px",
    "& .MuiChip-label": {
        padding: "0px 8px",
        fontSize: "0.65rem",
    },
}));

const GradientBox = styled(Box)<{ gradient: string }>(({ gradient }) => ({
    background: gradient,
    borderRadius: 8,
    padding: 16,
    color: "white",
    position: "relative",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        top: -30,
        right: -30,
        width: 120,
        height: 120,
        borderRadius: "50%",
        background: "rgba(255,255,255,0.1)",
    },
}));

const PulsingDot = styled(Box)({
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: "#4caf50",
    boxShadow: "0 0 0 0 rgba(76, 175, 80, 1)",
    animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
    "@keyframes pulse": {
        "0%": {
            transform: "scale(1)",
            boxShadow: "0 0 0 0 rgba(76, 175, 80, 0.7)",
        },
        "70%": {
            transform: "scale(1)",
            boxShadow: "0 0 0 8px rgba(76, 175, 80, 0)",
        },
        "100%": {
            transform: "scale(1)",
            boxShadow: "0 0 0 0 rgba(76, 175, 80, 0)",
        },
    },
});

const StatCard: FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    gradient: string;
    subtitle?: string;
    comparison?: number | null;
}> = ({ title, value, icon, gradient, subtitle, comparison }) => (
    <StyledCard>
        <GradientBox gradient={gradient}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ opacity: 0.9, mb: 0.5, fontSize: '0.75rem' }}>
                        {title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1.75rem' }}>
                        {value}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={0.5}>
                        {subtitle && (
                            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
                                {subtitle}
                            </Typography>
                        )}
                        {comparison !== null && comparison !== undefined && comparison !== 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                    borderRadius: 1.5,
                                    px: 1.5,
                                    py: 0.5,
                                    border: `2px solid ${comparison >= 0 ? '#4caf50' : '#f44336'}`,
                                    boxShadow: `0 2px 12px ${comparison >= 0 ? 'rgba(76, 175, 80, 0.4)' : 'rgba(244, 67, 54, 0.4)'}`,
                                }}
                            >
                                <CompareArrowsIcon 
                                    sx={{ 
                                        fontSize: 16, 
                                        color: comparison >= 0 ? '#4caf50' : '#f44336',
                                        fontWeight: 'bold'
                                    }} 
                                />
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        fontSize: '0.9rem',
                                        fontWeight: 700,
                                        color: comparison >= 0 ? '#2e7d32' : '#c62828',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    {comparison >= 0 ? '+' : ''}{comparison.toFixed(1)}%
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
                <Box
                    sx={{
                        backgroundColor: "rgba(255,255,255,0.2)",
                        borderRadius: 1.5,
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {icon}
                </Box>
            </Box>
        </GradientBox>
    </StyledCard>
);

const DepartmentCard: FC<{
    departmentName: string;
    averageHours: number;
    totalHours: number;
    employeeCount: number;
    latestPublishedShiftDate: Date | string | null;
    t: (key: string) => string;
    getDateLocale: () => Locale;
}> = ({ departmentName, averageHours, totalHours, employeeCount, latestPublishedShiftDate, t, getDateLocale }) => {
    const today = new Date();
    const latestDate = latestPublishedShiftDate
        ? (latestPublishedShiftDate instanceof Date ? latestPublishedShiftDate : new Date(latestPublishedShiftDate))
        : null;
    const daysUntilLatest = latestDate
        ? differenceInDays(latestDate, today)
        : null;
    const isNegative = daysUntilLatest !== null && daysUntilLatest < 0;
    const isWarning = daysUntilLatest !== null && daysUntilLatest >= 0 && daysUntilLatest < 7;

    return (
        <StyledCard>
            <CardContent sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                        {departmentName}
                    </Typography>
                    <Chip
                        label={`${employeeCount} ${employeeCount === 1 ? t("shiftStatistics.employee") : t("shiftStatistics.employees")}`}
                        size="small"
                        color="primary"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                </Box>
                <Box mb={1.5}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {t("shiftStatistics.averageDailyHours")}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>
                            {averageHours.toFixed(1)}h
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min((averageHours / 8) * 100, 100)}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: alpha("#1976d2", 0.1),
                            "& .MuiLinearProgress-bar": {
                                borderRadius: 3,
                                background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                            },
                        }}
                    />
                </Box>
                <Typography variant="caption" color="text.secondary" mb={1} sx={{ fontSize: '0.7rem' }}>
                    {t("shiftStatistics.totalHours")}: <strong>{totalHours.toFixed(1)}h</strong>
                </Typography>
                {latestDate && (
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        mt={1}
                        p={0.75}
                        sx={{
                            backgroundColor: isNegative 
                                ? alpha("#f44336", 0.1) 
                                : isWarning 
                                    ? alpha("#ff9800", 0.1) 
                                    : alpha("#4caf50", 0.1),
                            borderRadius: 1,
                            border: `1px solid ${
                                isNegative 
                                    ? alpha("#f44336", 0.3) 
                                    : isWarning 
                                        ? alpha("#ff9800", 0.3) 
                                        : alpha("#4caf50", 0.3)
                            }`,
                        }}
                    >
                        {(isNegative || isWarning) && <WarningIcon sx={{ fontSize: 14, color: isNegative ? "#f44336" : "#ff9800" }} />}
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            {t("shiftStatistics.latestPublishedShift")}:{" "}
                            <strong style={{ 
                                color: isNegative 
                                    ? "#f44336" 
                                    : isWarning 
                                        ? "#ff9800" 
                                        : "#4caf50" 
                            }}>
                                {format(latestDate, "MMM dd, yyyy", { locale: getDateLocale() })}
                            </strong>
                            {daysUntilLatest !== null && (
                                <span style={{ 
                                    marginLeft: 4,
                                    color: isNegative 
                                        ? "#f44336" 
                                        : isWarning 
                                            ? "#ff9800" 
                                            : "#4caf50"
                                }}>
                                    ({daysUntilLatest > 0 ? `+${daysUntilLatest}` : daysUntilLatest} {t("shiftStatistics.days")})
                                </span>
                            )}
                        </Typography>
                    </Box>
                )}
                {!latestPublishedShiftDate && (
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        mt={1}
                        p={0.75}
                        sx={{
                            backgroundColor: alpha("#f44336", 0.1),
                            borderRadius: 1,
                            border: `1px solid ${alpha("#f44336", 0.3)}`,
                        }}
                    >
                        <WarningIcon sx={{ fontSize: 14, color: "#f44336" }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            {t("shiftStatistics.noPublishedShifts")}
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </StyledCard>
    );
};

const ShiftsDashboard: FC = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();

    // Get the appropriate date-fns locale based on current language
    const getDateLocale = () => {
        switch (i18n.language) {
            case 'de':
                return de;
            case 'fr':
                return fr;
            case 'it':
                return it;
            default:
                return enUS;
        }
    };
    const [startDate, setStartDate] = useState<Date>(new Date());
    const [endDate, setEndDate] = useState<Date>(new Date());
    const [statistics, setStatistics] = useState<ShiftStatisticsEntity | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Helper function to format date as YYYY-MM-DD using local date to avoid timezone issues
    // This ensures the date the user selected is the date sent to the backend
    const formatDateForBackend = (date: Date): string => {
        // Use format from date-fns which works with local dates
        // This ensures we get the date the user sees, not the UTC date
        return format(date, 'yyyy-MM-dd');
    };

    const fetchStatistics = useCallback(async (start?: Date, end?: Date) => {
        try {
            setLoading(true);
            setError(null);
            const startToUse = start || startDate;
            const endToUse = end || endDate;
            // Send just the date string (YYYY-MM-DD) to avoid timezone conversion issues
            // The backend will parse this as a date in its timezone
            const response = await shiftApi.getShiftStatistics(
                formatDateForBackend(startToUse),
                formatDateForBackend(endToUse)
            );
            // Convert date strings to Date objects if needed
            const data: any = response.data;
            if (data.startDate && typeof data.startDate === 'string') {
                data.startDate = new Date(data.startDate);
            }
            if (data.endDate && typeof data.endDate === 'string') {
                data.endDate = new Date(data.endDate);
            }
            // Convert latestPublishedShiftDate for each department
            if (data.averageDailyHoursByDepartment) {
                data.averageDailyHoursByDepartment = data.averageDailyHoursByDepartment.map((dept: any) => ({
                    ...dept,
                    latestPublishedShiftDate: dept.latestPublishedShiftDate
                        ? (typeof dept.latestPublishedShiftDate === 'string'
                            ? new Date(dept.latestPublishedShiftDate)
                            : dept.latestPublishedShiftDate)
                        : null,
                }));
            }
            setStatistics(data as ShiftStatisticsEntity);
        } catch (err: any) {
            console.error("Error fetching shift statistics:", err);
            setError(err?.response?.data?.message || t("shiftStatistics.loadError"));
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, t]);

    useEffect(() => {
        fetchStatistics();
    }, [fetchStatistics]);

    // Auto-refresh every minute for live data
    useEffect(() => {
        const interval = setInterval(() => {
            fetchStatistics();
        }, 60000); // 60 seconds = 1 minute

        // Cleanup interval on unmount
        return () => clearInterval(interval);
    }, [fetchStatistics]); // Re-create interval if fetchStatistics changes

    const handlePreviousPeriod = () => {
        const periodDays = differenceInDays(endDate, startDate) + 1;
        setEndDate(subDays(startDate, 1));
        setStartDate(subDays(startDate, periodDays));
    };

    const handleNextPeriod = () => {
        const periodDays = differenceInDays(endDate, startDate) + 1;
        setStartDate(addDays(endDate, 1));
        setEndDate(addDays(endDate, periodDays));
    };

    const handleToday = () => {
        const today = new Date();
        setStartDate(today);
        setEndDate(today);
    };

    const handleQuickSelect = (days: number) => {
        const today = new Date();
        setStartDate(subDays(today, days - 1));
        setEndDate(today);
    };

    const isToday = isSameDay(startDate, new Date()) && isSameDay(endDate, new Date());
    
    // Check if current selection matches quick select options
    const isLast7Days = (() => {
        const today = new Date();
        const expectedStart = subDays(today, 6);
        return isSameDay(startDate, expectedStart) && isSameDay(endDate, today);
    })();
    
    const isLast30Days = (() => {
        const today = new Date();
        const expectedStart = subDays(today, 29);
        return isSameDay(startDate, expectedStart) && isSameDay(endDate, today);
    })();

    // Generate 15-minute interval categories (96 intervals per day)
    const generateIntervalCategories = () => {
        const categories: string[] = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let quarter = 0; quarter < 4; quarter++) {
                const minute = quarter * 15;
                categories.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
            }
        }
        return categories;
    };

    // Chart options for 15-minute interval employee count
    const intervalChartOptions: ApexOptions = {
        chart: {
            type: "area",
            height: 350,
            toolbar: { show: false },
            zoom: { enabled: false },
        },
        dataLabels: { enabled: false },
        stroke: {
            curve: "smooth",
            width: 2,
        },
        fill: {
            type: "gradient",
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.7,
                opacityTo: 0.3,
                stops: [0, 100],
                colorStops: [
                    {
                        offset: 0,
                        color: "#667eea",
                        opacity: 1,
                    },
                    {
                        offset: 100,
                        color: "#764ba2",
                        opacity: 1,
                    },
                ],
            },
        },
         xaxis: {
             categories: generateIntervalCategories(),
             labels: {
                 style: { colors: theme.palette.text.secondary, fontSize: "10px" },
                 rotate: -45,
                 rotateAlways: false,
                 showDuplicates: false,
                 formatter: (value: string) => {
                     // Show only every 4th label (hourly) to avoid clutter
                     if (!value || typeof value !== 'string') {
                         return '';
                     }
                     const parts = value.split(':');
                     if (parts.length < 2) {
                         return '';
                     }
                     const hour = parseInt(parts[0], 10);
                     const minute = parseInt(parts[1], 10);
                     if (isNaN(hour) || isNaN(minute)) {
                         return '';
                     }
                     if (minute === 0) {
                         return `${hour}:00`;
                     }
                     return '';
                 },
             },
         },
        yaxis: {
            title: { text: t("shiftStatistics.averageEmployees") },
            labels: {
                style: { colors: theme.palette.text.secondary },
            },
        },
        grid: {
            borderColor: alpha(theme.palette.divider, 0.1),
        },
        tooltip: {
            theme: theme.palette.mode,
            y: {
                formatter: (val: number) => `${val.toFixed(1)} ${val === 1 ? t("shiftStatistics.employee") : t("shiftStatistics.employees")}`,
            },
        },
        colors: ["#667eea"],
    };

    if (loading && !statistics) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="60vh"
                flexDirection="column"
                gap={2}
            >
                <CircularProgress size={60} />
                <Typography variant="h6" color="text.secondary">
                    {t("shiftStatistics.loading")}
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="60vh"
                flexDirection="column"
                gap={2}
            >
                <Typography variant="h6" color="error">
                    {error}
                </Typography>
                <IconButton onClick={async () => await fetchStatistics()} color="primary">
                    <RefreshIcon />
                </IconButton>
            </Box>
        );
    }

    if (!statistics) {
        return null;
    }

    const intervalData = statistics.intervalEmployeeCount.map((item) => item.averageEmployeeCount);
    const periodDays = statistics.periodDays || 1;
    const startDateObj = typeof statistics.startDate === 'string'
        ? new Date(statistics.startDate)
        : (statistics.startDate as Date);
    const endDateObj = typeof statistics.endDate === 'string'
        ? new Date(statistics.endDate)
        : (statistics.endDate as Date);
    const periodLabel = periodDays === 1
        ? format(startDateObj, "MMM dd, yyyy", { locale: getDateLocale() })
        : `${format(startDateObj, "MMM dd, yyyy", { locale: getDateLocale() })} - ${format(endDateObj, "MMM dd, yyyy", { locale: getDateLocale() })}`;

    return (
        <Box sx={{ p: 3 }}>
            {/* Header with Date Navigation */}
            <Box
                sx={{
                    mb: 4,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                }}
            >
                <Box>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <Typography variant="h4" fontWeight={700}>
                            {t("shiftStatistics.dashboardTitle")}
                        </Typography>
                        <BetaChip label="BETA" size="small" />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        {t("shiftStatistics.dashboardSubtitle")} • {periodLabel}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateLocale()}>
                        <DatePicker
                            label={t("shiftStatistics.startDate")}
                            value={startDate}
                            onChange={(newValue) => {
                                if (newValue) {
                                    const newStart = startOfDay(newValue);
                                    if (newStart <= endDate) {
                                        setStartDate(newStart);
                                    }
                                }
                            }}
                            format="dd/MM/yyyy"
                            slotProps={{
                                textField: {
                                    size: "small",
                                    sx: { width: 140 },
                                },
                            }}
                        />
                        <DatePicker
                            label={t("shiftStatistics.endDate")}
                            value={endDate}
                            onChange={(newValue) => {
                                if (newValue) {
                                    const newEnd = endOfDay(newValue);
                                    if (newEnd >= startDate) {
                                        setEndDate(newEnd);
                                    }
                                }
                            }}
                            format="dd/MM/yyyy"
                            slotProps={{
                                textField: {
                                    size: "small",
                                    sx: { width: 140 },
                                },
                            }}
                        />
                    </LocalizationProvider>
                    <Box display="flex" gap={0.5}>
                        <Chip
                            label={t("shiftStatistics.today")}
                            size="small"
                            onClick={handleToday}
                            color={isToday ? "primary" : "default"}
                            sx={{ cursor: "pointer" }}
                        />
                        <Chip
                            label={t("shiftStatistics.last7Days")}
                            size="small"
                            onClick={() => handleQuickSelect(7)}
                            color={isLast7Days ? "primary" : "default"}
                            sx={{ cursor: "pointer" }}
                        />
                        <Chip
                            label={t("shiftStatistics.last30Days")}
                            size="small"
                            onClick={() => handleQuickSelect(30)}
                            color={isLast30Days ? "primary" : "default"}
                            sx={{ cursor: "pointer" }}
                        />
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <IconButton onClick={handlePreviousPeriod} color="primary" size="small">
                            <ArrowBackIcon />
                        </IconButton>
                        <IconButton
                            onClick={handleNextPeriod}
                            color="primary"
                            size="small"
                            disabled={isToday}
                        >
                            <ArrowForwardIcon />
                        </IconButton>
                        <IconButton onClick={async () => await fetchStatistics()} color="primary" size="small">
                            <RefreshIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Current Employees On Duty */}
                <Grid item xs={12} md={4}>
                    <StyledCard>
                        <GradientBox gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                <Box sx={{ flex: 1 }}>
                                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                        <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>
                                            {t("shiftStatistics.employeesOnDuty")}
                                        </Typography>
                                        <PulsingDot />
                                    </Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1.75rem' }}>
                                        {statistics.totalEmployeesOnDuty}
                                    </Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
                                        {t("shiftStatistics.currentlyWorking")}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        backgroundColor: "rgba(255,255,255,0.2)",
                                        borderRadius: 1.5,
                                        p: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <PeopleIcon sx={{ fontSize: 24 }} />
                                </Box>
                            </Box>
                        </GradientBox>
                    </StyledCard>
                </Grid>

                {/* Total Shifts Scheduled */}
                <Grid item xs={12} md={4}>
                    <StatCard
                        title={t("shiftStatistics.totalShiftsScheduled")}
                        value={statistics.totalShiftsScheduled}
                        icon={<ScheduleIcon sx={{ fontSize: 24 }} />}
                        gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                        subtitle={t("shiftStatistics.forThisDate")}
                    />
                </Grid>

                {/* Total Hours Worked */}
                <Grid item xs={12} md={4}>
                    <StatCard
                        title={t("shiftStatistics.totalHoursWorked")}
                        value={`${statistics.totalHoursWorked.toFixed(1)}h`}
                        icon={<AccessTimeIcon sx={{ fontSize: 24 }} />}
                        gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
                        subtitle={t("shiftStatistics.acrossAllDepartments")}
                        comparison={statistics.previousPeriodComparison}
                    />
                </Grid>

                {/* Peak Interval */}
                <Grid item xs={12} md={6}>
                    <StyledCard>
                        <CardContent sx={{ p: 2 }}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Box
                                    sx={{
                                        backgroundColor: alpha("#ff6b6b", 0.1),
                                        borderRadius: 1.5,
                                        p: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <TrendingUpIcon sx={{ fontSize: 24, color: "#ff6b6b" }} />
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        {t("shiftStatistics.peakInterval")}
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.5rem' }}>
                                        {statistics.peakInterval}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                        {statistics.peakIntervalEmployeeCount.toFixed(1)} {t("shiftStatistics.averageEmployees")}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </StyledCard>
                </Grid>

                {/* Average Daily Hours All Employees */}
                <Grid item xs={12} md={6}>
                    <StatCard
                        title={t("shiftStatistics.averageDailyHoursAllEmployees")}
                        value={`${statistics.averageDailyHoursAllEmployees.toFixed(1)}h`}
                        icon={<AccessTimeIcon sx={{ fontSize: 24 }} />}
                        gradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
                        subtitle={t("shiftStatistics.perEmployeePerDay")}
                        comparison={statistics.previousPeriodComparison}
                    />
                </Grid>

                {/* Average Daily Employee Count */}
                <Grid item xs={12} md={6}>
                    <StatCard
                        title={t("shiftStatistics.averageDailyEmployeeCount")}
                        value={statistics.averageDailyEmployeeCount.toFixed(1)}
                        icon={<PeopleIcon sx={{ fontSize: 24 }} />}
                        gradient="linear-gradient(135deg, #30cfd0 0%, #330867 100%)"
                        subtitle={t("shiftStatistics.overPeriod")}
                        comparison={statistics.previousPeriodComparison}
                    />
                </Grid>


                {/* Average Shift Duration */}
                <Grid item xs={12} md={6}>
                    <StyledCard>
                        <CardContent sx={{ p: 2 }}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Box
                                    sx={{
                                        backgroundColor: alpha("#4ecdc4", 0.1),
                                        borderRadius: 1.5,
                                        p: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <AccessTimeIcon sx={{ fontSize: 24, color: "#4ecdc4" }} />
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        {t("shiftStatistics.averageShiftDuration")}
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.5rem' }}>
                                        {statistics.averageShiftDuration.toFixed(1)}h
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                        {t("shiftStatistics.perShift")}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </StyledCard>
                </Grid>

                {/* 15-Minute Interval Employee Count Chart */}
                <Grid item xs={12}>
                    <StyledCard>
                        <CardContent sx={{ p: 2 }}>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                <BarChartIcon color="primary" sx={{ fontSize: 20 }} />
                                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                                    {t("shiftStatistics.intervalEmployeeCount")}
                                </Typography>
                                <Chip
                                    label={t("shiftStatistics.averageOverPeriod")}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                />
                            </Box>
                            <Chart
                                options={intervalChartOptions}
                                series={[
                                    {
                                        name: t("shiftStatistics.averageEmployees"),
                                        data: intervalData,
                                    },
                                ]}
                                type="area"
                                height={350}
                            />
                        </CardContent>
                    </StyledCard>
                </Grid>

                {/* Published vs Unpublished Shifts */}
                <Grid item xs={12} md={6}>
                    <StyledCard>
                        <CardContent sx={{ p: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600} mb={2} sx={{ fontSize: '0.95rem' }}>
                                {t("shiftStatistics.shiftPublicationStatus")}
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={1.5}>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    p={1.5}
                                    sx={{
                                        backgroundColor: alpha("#4caf50", 0.1),
                                        borderRadius: 1.5,
                                    }}
                                >
                                    <Box display="flex" alignItems="center" gap={0.75}>
                                        <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 20 }} />
                                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                                            {t("shiftStatistics.published")}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight={700} color="#4caf50" sx={{ fontSize: '1.25rem' }}>
                                        {statistics.publishedShifts}
                                    </Typography>
                                </Box>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    p={1.5}
                                    sx={{
                                        backgroundColor: alpha("#ff9800", 0.1),
                                        borderRadius: 1.5,
                                    }}
                                >
                                    <Box display="flex" alignItems="center" gap={0.75}>
                                        <CancelIcon sx={{ color: "#ff9800", fontSize: 20 }} />
                                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                                            {t("shiftStatistics.unpublished")}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight={700} color="#ff9800" sx={{ fontSize: '1.25rem' }}>
                                        {statistics.unpublishedShifts}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </StyledCard>
                </Grid>

                {/* Employees On Duty By Department */}
                <Grid item xs={12}>
                    <StyledCard>
                        <CardContent sx={{ p: 2 }}>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                <BusinessIcon color="primary" sx={{ fontSize: 20 }} />
                                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                                    {t("shiftStatistics.employeesOnDutyByDepartment")}
                                </Typography>
                                <PulsingDot />
                            </Box>
                            <Grid container spacing={1.5}>
                                {statistics.employeesOnDutyByDepartment.map((dept) => (
                                    <Grid item xs={12} sm={6} md={4} key={dept.departmentId}>
                                        <Box
                                            p={1.5}
                                            sx={{
                                                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                                borderRadius: 1.5,
                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                            }}
                                        >
                                            <Typography variant="caption" color="text.secondary" mb={0.5} sx={{ fontSize: '0.7rem' }}>
                                                {dept.departmentName}
                                            </Typography>
                                            <Typography variant="h6" fontWeight={700} color="primary" sx={{ fontSize: '1.25rem' }}>
                                                {dept.employeesOnDuty}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </CardContent>
                    </StyledCard>
                </Grid>

                {/* Average Daily Hours By Department */}
                <Grid item xs={12}>
                    <StyledCard>
                        <CardContent sx={{ p: 2 }}>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                <ShowChartIcon color="primary" sx={{ fontSize: 20 }} />
                                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                                    {t("shiftStatistics.averageDailyHoursByDepartment")}
                                </Typography>
                            </Box>
                            <Grid container spacing={2}>
                                {statistics.averageDailyHoursByDepartment.map((dept) => (
                                    <Grid item xs={12} sm={6} md={4} key={dept.departmentId}>
                                        <DepartmentCard
                                            departmentName={dept.departmentName}
                                            averageHours={dept.averageDailyHours}
                                            totalHours={dept.totalHours}
                                            employeeCount={dept.employeeCount}
                                            latestPublishedShiftDate={dept.latestPublishedShiftDate}
                                            t={t}
                                            getDateLocale={getDateLocale}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </CardContent>
                    </StyledCard>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ShiftsDashboard;

